// Pure planners/resolvers for the AI Tournament Assistant's GHIN/handicap
// intents. No React/Firebase/GHIN-API — the executor handlers wrap these with
// the actual DB reads, GHIN lookups, writes, and re-snapshot. Kept pure so the
// batch/resolution logic is unit-testable under `node --test`.

const norm = (s) => String(s || '').trim().toLowerCase()

// Default name matcher for the roster fallback: full-name equality OR a shared
// last name. The executor injects its own richer fuzzyMatchName at call time.
function defaultMatch(a, b) {
  const na = norm(a), nb = norm(b)
  if (!na || !nb) return false
  if (na === nb) return true
  const la = na.split(/\s+/).pop(), lb = nb.split(/\s+/).pop()
  return la === lb && la.length > 1
}

// Decide, per registered player, what "pull GHINs for the field" should do.
//   already source:'ghin'      → alreadyVerified (left as-is)
//   has a GHIN number          → toPull  (overwrites a self-reported index)
//   no GHIN number anywhere     → skipped
// GHIN number source: registration playerHandicaps[i].ghin, else the club
// roster matched by name. overwriteSelf=false keeps an existing self index.
export function planGhinPull(registrations, roster = {}, { overwriteSelf = true, matchName = defaultMatch } = {}) {
  const rosterList = Object.values(roster || {}).map((r) => ({ name: r?.name || '', ghin: r?.ghin || null }))
  const toPull = []
  const skipped = []
  const alreadyVerified = []
  for (const [regId, reg] of Object.entries(registrations || {})) {
    const names = reg?.players || []
    const phs = reg?.playerHandicaps || []
    names.forEach((nm, i) => {
      const entry = phs?.[i]
      if (entry?.source === 'ghin') { alreadyVerified.push(nm); return }
      const hasSelfIndex = Number.isFinite(Number(entry?.handicapIndex))
      if (!overwriteSelf && hasSelfIndex) { alreadyVerified.push(nm); return }
      let ghin = entry?.ghin || null
      if (!ghin) {
        const hit = rosterList.find((r) => r.ghin && matchName(nm, r.name))
        if (hit) ghin = hit.ghin
      }
      if (!ghin) { skipped.push(nm); return }
      toPull.push({ regId, playerIndex: i, name: nm, ghin })
    })
  }
  return { toPull, skipped, alreadyVerified }
}

// Resolve a director's spoken name to a single registration slot.
//   → { regId, playerIndex, name }   (unique match)
//   → { ambiguous: [names] }         (matches >1 slot — ask to clarify)
//   → null                           (no match)
export function resolveRegistrationPlayer(registrations, name, matchName = defaultMatch) {
  const hits = []
  for (const [regId, reg] of Object.entries(registrations || {})) {
    const names = reg?.players || []
    names.forEach((nm, i) => { if (matchName(name, nm)) hits.push({ regId, playerIndex: i, name: nm }) })
  }
  if (hits.length === 0) return null
  if (hits.length > 1) return { ambiguous: hits.map((h) => h.name) }
  return hits[0]
}

// Compose the spoken answer for "who's self-reported / how many aren't verified".
export function summarizeHandicapReview(review) {
  const { rows = [], counts = {} } = review || {}
  const g = counts.ghin || 0, s = counts.self || 0, n = counts.none || 0, total = counts.total || 0
  const selfNames = rows.filter((r) => r.source === 'self').map((r) => r.name)
  let msg = `${g} GHIN-verified · ${s} self-reported · ${n} with no index · ${total} player${total === 1 ? '' : 's'} total.`
  if (s > 0) msg += ` Self-reported: ${selfNames.join(', ')}.`
  else if (total > 0) msg += ' Everyone with an index is GHIN-verified.'
  return msg
}
